import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { login, seedSuperadmin } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Loader2, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await login(email, password);
      const { access_token, user } = response.data;
      loginUser(access_token, user);
      toast.success(`Bienvenido, ${user.name}`);
      
      // Navigate based on role
      if (user.role === 'staff') {
        navigate('/queries');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const response = await seedSuperadmin();
      toast.success(response.data.message);
    } catch (error) {
      console.error('Seed error:', error);
      toast.info(error.response?.data?.detail || 'Superadmin ya existe');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.9)), url('https://images.unsplash.com/photo-1656688797048-595e63befa85?auto=format&fit=crop&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Card className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-2xl border-0" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] tracking-tight">
              VITÁCORE
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">creado por Joan Monzo</p>
            <p className="text-sm text-slate-500 mt-1">Sistema de Gestion de Tareas</p>
          </div>
          <div className="h-1 w-16 bg-emerald-700 mx-auto rounded-full" />
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 text-sm font-medium">
                Correo electronico
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 border-stone-200 focus:ring-2 focus:ring-slate-900"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 text-sm font-medium">
                Contrasena
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 border-stone-200 focus:ring-2 focus:ring-slate-900"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium transition-all"
              data-testid="login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Iniciando...
                </>
              ) : (
                'Iniciar Sesion'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-stone-200">
            <p className="text-center text-xs text-slate-500 mb-3">
              Primera vez? Configura el sistema:
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handleSeed}
              disabled={seeding}
              className="w-full text-slate-600 hover:text-slate-900 border-stone-200"
              data-testid="seed-btn"
            >
              {seeding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                'Crear Usuario Inicial'
              )}
            </Button>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            desarrollado por Joan Monzo © 2026
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
